import { useEffect, useState } from 'react';
import {
  personPasst,
  ROLLE,
  type Einrichtung,
  type Gruppenabgleich,
  type Spiegelabgleich,
  type Gruppenrolle,
  type Lauf,
  type Rolle,
  type TaetigkeitsartAnsicht,
} from '@arbeitsrapporte/shared';
import { api } from '../api';
import { useIch } from '../App';
import { Fehler } from '../bausteine/Meldung';
import { Personenzeile } from '../bausteine/Mitarbeiterwahl';
import { Taetigkeitsartenpflege } from './Taetigkeitsarten';

/**
 * Alles, was eingerichtet wird – an einer Stelle, und nur für die Leitung.
 *
 * **Warum eine eigene Seite.** Die Tätigkeitsarten standen zwischen den
 * Reitern, die man täglich benutzt, obwohl sie ein paar Mal im Jahr
 * angefasst werden; der Zustand des Spiegels stand nirgends, und wie der
 * Dienst eingerichtet ist, sah man nur auf der Maschine. Wer eingerichtet
 * hat, weiß wonach er sucht – wer es nicht getan hat, sucht an fünf
 * Stellen.
 *
 * **Warum die Hälfte davon nur zu lesen ist.** Mandant, Audience, Secret
 * und Hub-Token stehen in `infra/.env`. Ein Formular hier gaukelte vor, man
 * könne sie ändern. Was dasteht, beantwortet die eine Frage, die beim
 * Einrichten zählt: Ist es angekommen? Die Ausnahme ist die Zuordnung
 * Gruppe → Rolle – die pflegt der Admin hier; fest bleibt nur die
 * Admin-Gruppe aus der `.env`.
 */

const ROLLENNAME: Record<Rolle, string> = {
  techniker: 'Techniker',
  leitung: 'Leitung',
  admin: 'Admin',
};

/** Eine gepflegte Zuordnung – zu lesen, oder mit „Ändern" Rolle und Bezeichnung neu setzen. */
function Zuordnungszeile({
  g,
  laeuft,
  tu,
}: {
  g: Gruppenrolle;
  laeuft: boolean;
  tu: (schritt: () => Promise<Gruppenrolle[]>) => Promise<boolean>;
}) {
  const [bearbeiten, setBearbeiten] = useState(false);
  const [rolle, setRolle] = useState<Rolle>(g.rolle);
  const [bezeichnung, setBezeichnung] = useState(g.bezeichnung);

  const anker = g.quelle === 'umgebung';

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2">
      {bearbeiten ? (
        <select
          aria-label="Rolle"
          className="feld w-36"
          value={rolle}
          onChange={(e) => setRolle(e.target.value as Rolle)}
        >
          {ROLLE.map((r) => (
            <option key={r} value={r}>
              {ROLLENNAME[r]}
            </option>
          ))}
        </select>
      ) : (
        <span className="w-20 shrink-0 text-sm font-medium">{ROLLENNAME[g.rolle]}</span>
      )}
      <span className="min-w-0 flex-1">
        {bearbeiten ? (
          <input
            aria-label="Bezeichnung"
            className="feld mb-1"
            placeholder="Bezeichnung (optional)"
            value={bezeichnung}
            onChange={(e) => setBezeichnung(e.target.value)}
          />
        ) : (
          g.bezeichnung && <span className="block text-sm">{g.bezeichnung}</span>
        )}
        <code className="text-sm break-all">{g.gruppe}</code>
        {!anker && g.eingetragenAm && (
          <span className="block text-xs text-slate-500">
            {g.eingetragenVon ? `eingetragen von ${g.eingetragenVon}` : 'aus .env übernommen'} am{' '}
            {new Date(g.eingetragenAm).toLocaleString('de-CH')}
          </span>
        )}
      </span>
      {anker ? (
        <span
          className="text-xs text-slate-500"
          title="Admin-Gruppe aus infra/.env (ENTRA_GRUPPE_ADMIN) – der Anker gegen das Aussperren, nur dort zu ändern."
        >
          fest (.env)
        </span>
      ) : bearbeiten ? (
        <span className="flex gap-2">
          <button
            type="button"
            className="knopf-haupt"
            disabled={laeuft}
            onClick={async () => {
              if (await tu(() => api.aendereGruppenzuordnung(g.id!, { rolle, bezeichnung }))) {
                setBearbeiten(false);
              }
            }}
          >
            Speichern
          </button>
          <button
            type="button"
            className="knopf"
            disabled={laeuft}
            onClick={() => {
              setRolle(g.rolle);
              setBezeichnung(g.bezeichnung);
              setBearbeiten(false);
            }}
          >
            Abbrechen
          </button>
        </span>
      ) : (
        <span className="flex gap-2">
          <button
            type="button"
            className="knopf"
            disabled={laeuft}
            onClick={() => setBearbeiten(true)}
          >
            Ändern
          </button>
          <button
            type="button"
            className="knopf-gefahr"
            disabled={laeuft}
            onClick={() => void tu(() => api.entferneGruppenzuordnung(g.id!))}
          >
            Entfernen
          </button>
        </span>
      )}
    </li>
  );
}

/**
 * Welche Entra-Gruppe welche Rolle bedeutet – die Admin-Gruppe aus der
 * `.env` fest, alle anderen mit „Ändern" und „Entfernen".
 */
function Gruppenzuordnung({ anfang, fertig }: { anfang: Gruppenrolle[]; fertig: () => void }) {
  const [gruppen, setGruppen] = useState(anfang);
  const [gruppe, setGruppe] = useState('');
  const [rolle, setRolle] = useState<Rolle>('techniker');
  const [bezeichnung, setBezeichnung] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);

  useEffect(() => setGruppen(anfang), [anfang]);

  const tu = async (schritt: () => Promise<Gruppenrolle[]>) => {
    setLaeuft(true);
    setFehler(null);

    try {
      setGruppen(await schritt());
      fertig();

      return true;
    } catch (f) {
      setFehler(f);

      return false;
    } finally {
      setLaeuft(false);
    }
  };

  const trageEin = async () => {
    if (await tu(() => api.ordneGruppeZu({ gruppe, rolle, bezeichnung }))) {
      setGruppe('');
      setBezeichnung('');
    }
  };

  return (
    <div className="space-y-3">
      {gruppen.length === 0 ? (
        <p className="text-sm text-amber-700">
          keine – es zählen nur App-Rollen aus dem `roles`-Anspruch
        </p>
      ) : (
        <ul className="divide-y divide-slate-100 rounded border border-slate-200">
          {gruppen.map((g) => (
            <Zuordnungszeile key={g.id ?? `anker-${g.gruppe}`} g={g} laeuft={laeuft} tu={tu} />
          ))}
        </ul>
      )}

      <Fehler fehler={fehler} />

      <form
        className="grid gap-2 sm:grid-cols-[1fr_10rem_1fr_auto] sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          void trageEin();
        }}
      >
        <div>
          <label className="feldname" htmlFor="gruppe-id">
            Objekt-ID der Gruppe
          </label>
          <input
            id="gruppe-id"
            className="feld font-mono"
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            value={gruppe}
            onChange={(e) => setGruppe(e.target.value)}
          />
        </div>
        <div>
          <label className="feldname" htmlFor="gruppe-rolle">
            Rolle
          </label>
          <select
            id="gruppe-rolle"
            className="feld"
            value={rolle}
            onChange={(e) => setRolle(e.target.value as Rolle)}
          >
            {ROLLE.map((r) => (
              <option key={r} value={r}>
                {ROLLENNAME[r]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="feldname" htmlFor="gruppe-name">
            Bezeichnung (optional)
          </label>
          <input
            id="gruppe-name"
            className="feld"
            placeholder="z. B. Rapport Techniker CH"
            value={bezeichnung}
            onChange={(e) => setBezeichnung(e.target.value)}
          />
        </div>
        <button type="submit" className="knopf-haupt" disabled={laeuft || !gruppe.trim()}>
          Zuordnen
        </button>
      </form>
    </div>
  );
}

/** Ein Zustand als Marke: eingerichtet oder nicht, in derselben Sprache wie der Rapportstatus. */
function Zustand({ gut, ja, nein }: { gut: boolean; ja: string; nein: string }) {
  return (
    <span
      className={`shrink-0 rounded border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${
        gut
          ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
          : 'border-amber-300 bg-amber-50 text-amber-800'
      }`}
    >
      {gut ? ja : nein}
    </span>
  );
}

function Wert({ name, wert }: { name: string; wert: string | null }) {
  return (
    <>
      <dt className="text-sm text-slate-500">{name}</dt>
      <dd className="break-all text-slate-900">
        {wert ?? <span className="text-slate-400">– nicht gesetzt –</span>}
      </dd>
    </>
  );
}

/**
 * Der Gruppenabgleich von Hand – wer in Entra eine Gruppe oder eine
 * Berechtigung nachgetragen hat, sieht sofort, ob sie greift.
 */
function GruppenabgleichJetzt({ fertig }: { fertig: () => void }) {
  const [laeuft, setLaeuft] = useState(false);
  const [lauf, setLauf] = useState<Gruppenabgleich | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);

  const starte = async () => {
    setLaeuft(true);
    setLauf(null);
    setFehler(null);

    try {
      setLauf(await api.gruppenabgleich());
      fertig();
    } catch (f) {
      setFehler(f);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="mt-3 space-y-2">
      <button type="button" className="knopf" disabled={laeuft} onClick={() => void starte()}>
        {laeuft ? 'Gleicht ab …' : 'Jetzt abgleichen'}
      </button>
      <Fehler fehler={fehler} />
      {lauf &&
        (lauf.fehler ? (
          <p className="rounded-lg bg-rose-50 p-3 text-sm text-rose-800">
            Fehlgeschlagen – nichts geändert. {lauf.fehler}
          </p>
        ) : (
          <p className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">
            {lauf.gelesen} gelesen, {lauf.angelegt} angelegt, {lauf.vergeben} Rollen vergeben,{' '}
            {lauf.entzogen} entzogen.
          </p>
        ))}
    </div>
  );
}

function Einrichtungsblock({ e, fertig }: { e: Einrichtung; fertig: () => void }) {
  return (
    <div className="space-y-3">
      <div className="karte">
        <div className="mb-2 flex items-center gap-3">
          <h3 className="font-semibold text-slate-900">Anmeldung (Entra)</h3>
          <span className="ml-auto">
            <Zustand gut={e.entra.eingerichtet} ja="eingerichtet" nein="fehlt" />
          </span>
        </div>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-[max-content_1fr]">
          <Wert name="Mandant" wert={e.entra.mandant} />
          <Wert name="Audience" wert={e.entra.audience} />
        </dl>
        <h4 className="mt-4 mb-2 text-sm font-medium text-slate-700">Gruppen auf Rollen</h4>
        <Gruppenzuordnung anfang={e.entra.gruppen} fertig={fertig} />
        <p className="mt-2 text-sm text-slate-500">
          Ohne Mandant und Audience antwortet jede Route mit 401. Eine Zuordnung wirkt mit der
          nächsten Anfrage der Person, für alle anderen mit dem nächsten Gruppenabgleich. Fest steht
          nur die Admin-Gruppe aus
          <code className="mx-1">ENTRA_GRUPPE_ADMIN</code> – damit sich niemand aussperrt. Gruppen
          aus <code className="mx-1">ENTRA_GRUPPE_TECHNIKER</code> und
          <code className="mx-1">ENTRA_GRUPPE_LEITUNG</code> werden einmal übernommen und lassen
          sich danach hier ändern; eine entfernte kommt nicht wieder. Dazu gehört in der
          Registrierung ein Gruppenanspruch für das Zugriffstoken.
        </p>
      </div>

      <div className="karte">
        <div className="mb-2 flex items-center gap-3">
          <h3 className="font-semibold text-slate-900">Termine und Einladungen (Graph)</h3>
          <span className="ml-auto">
            <Zustand gut={e.graph.eingerichtet} ja="eingerichtet" nein="fehlt" />
          </span>
        </div>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-[max-content_1fr]">
          <Wert name="Einladendes Postfach" wert={e.graph.einladungPostfach} />
        </dl>
        {/*
         * Der Testmodus steht oben und in Rot: Er ist kein Wert unter
         * anderen, sondern der eine Zustand, in dem kein Kunde mehr eine
         * Einladung bekommt – und das fiele sonst erst auf, wenn einer
         * nicht erscheint.
         */}
        {e.graph.testEmpfaenger && (
          <p className="mt-3 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">
            <strong>Testmodus:</strong> Alle Einladungen gehen an{' '}
            <code>{e.graph.testEmpfaenger}</code> – kein Ansprechpartner eines Kunden bekommt eine.
            Der Betreff trägt „[TEST]“. Für den Betrieb
            <code className="mx-1">GRAPH_TEST_EMPFAENGER</code>
            in der <code>infra/.env</code> leeren und neu starten.
          </p>
        )}
        {!e.graph.eingerichtet && (
          <p className="mt-2 text-sm text-amber-700">
            Einsätze lassen sich planen, es entstehen aber weder Termine noch Einladungen. An der
            Einsatzkarte steht dann „Kein Termin im Kalender“.
          </p>
        )}
      </div>

      <div className="karte">
        <div className="mb-2 flex items-center gap-3">
          <h3 className="font-semibold text-slate-900">Gruppenabgleich (Graph)</h3>
          <span className="ml-auto">
            <Zustand gut={e.gruppenabgleich.eingerichtet} ja="eingerichtet" nein="fehlt" />
          </span>
        </div>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-[max-content_1fr]">
          <Wert
            name="Zuletzt gelaufen"
            wert={
              e.gruppenabgleich.zuletzt
                ? new Date(e.gruppenabgleich.zuletzt).toLocaleString('de-CH')
                : null
            }
          />
          <dt className="text-sm text-slate-500">Ergebnis</dt>
          <dd className="text-sm">
            {e.gruppenabgleich.ergebnis === 'fehlgeschlagen' ? (
              <span className="text-rose-800">
                fehlgeschlagen – nichts geändert
                {e.gruppenabgleich.fehler && (
                  <span className="mt-1 block break-words">{e.gruppenabgleich.fehler}</span>
                )}
              </span>
            ) : e.gruppenabgleich.ergebnis === 'erfolgreich' ? (
              <span className="text-emerald-700">erfolgreich</span>
            ) : (
              <span className="text-slate-400">– noch kein Lauf –</span>
            )}
          </dd>
        </dl>
        <p className="mt-2 text-sm text-slate-500">
          Stündlich liest der Dienst die Mitglieder der Rapport-Gruppen aus Entra: Wer neu darin
          steht, lässt sich einteilen, ohne sich vorher angemeldet zu haben; wer aus allen Gruppen
          fällt, verliert seine Rolle und steht nicht mehr in der Planung. Braucht
          <code className="mx-1">GroupMember.Read.All</code> und
          <code className="mx-1">User.Read.All</code> an der Registrierung.
        </p>
        {e.gruppenabgleich.eingerichtet ? (
          <GruppenabgleichJetzt fertig={fertig} />
        ) : (
          <p className="mt-2 text-sm text-amber-700">
            Ohne ihn entsteht ein Mitarbeiter erst mit seiner ersten Anmeldung, und vorher lässt er
            sich nicht einteilen.
          </p>
        )}
      </div>

      <div className="karte">
        <div className="mb-2 flex items-center gap-3">
          <h3 className="font-semibold text-slate-900">ERP-Hub</h3>
          <span className="ml-auto">
            <Zustand gut={e.hub.eingerichtet} ja="eingerichtet" nein="fehlt" />
          </span>
        </div>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-[max-content_1fr]">
          <Wert name="Adresse" wert={e.hub.url} />
          <dt className="text-sm text-slate-500">Schreibtoken</dt>
          <dd className="text-sm">
            <Zustand gut={e.hub.schreibtoken} ja="gesetzt" nein="fehlt" />
          </dd>
        </dl>
        <p className="mt-2 text-sm text-slate-500">
          Von dort kommen Aufträge, Kunden, Artikel und Ansprechpartner – und dorthin geht der
          freigegebene Rapport, mit einem eigenen Schreibtoken (HUB_WRITE_TOKEN; im Hub
          HUB_WRITE_TOKEN_ARBEITSRAPPORTE). Ohne es bleibt die Outbox stehen und sagt es unter
          „Versand".
        </p>
      </div>
    </div>
  );
}

/**
 * Der Spiegel-Lauf von Hand – wer drüben einen Artikel ins ERP übertragen
 * hat, sieht ihn sofort in der Auswahl statt nach der vollen Stunde.
 */
function SpiegelabgleichJetzt({ fertig }: { fertig: () => void }) {
  const [laeuft, setLaeuft] = useState(false);
  const [lauf, setLauf] = useState<Spiegelabgleich | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);

  const starte = async () => {
    setLaeuft(true);
    setLauf(null);
    setFehler(null);

    try {
      setLauf(await api.spiegelabgleich());
      fertig();
    } catch (f) {
      setFehler(f);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="mb-4 space-y-2">
      <button type="button" className="knopf" disabled={laeuft} onClick={() => void starte()}>
        {laeuft ? 'Gleicht ab …' : 'Jetzt mit dem Hub abgleichen'}
      </button>
      <Fehler fehler={fehler} />
      {lauf &&
        (lauf.fehler.length > 0 ? (
          <p className="rounded-lg bg-rose-50 p-3 text-sm text-rose-800">
            {lauf.fehler.map((f) => `${f.liste}: ${f.meldung}`).join(' · ')}
          </p>
        ) : (
          <p className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">
            Abgeglichen: {lauf.listen.map((l) => `${l.gelesen} ${l.liste}`).join(', ')} neu gelesen.
          </p>
        ))}
    </div>
  );
}

/** Der jüngste Lauf je Liste. Eine leere Auswahl sieht sonst genauso aus wie ein stehender Sync. */
function Spiegel({ laeufe }: { laeufe: Lauf[] }) {
  if (laeufe.length === 0) {
    return (
      <p className="text-amber-700">
        Noch kein Lauf. Solange keiner gelaufen ist, sind Auftrags- und Artikelauswahl leer.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-slate-500">
            <th className="px-3 py-2 font-medium">Liste</th>
            <th className="px-3 py-2 font-medium">Zuletzt</th>
            <th className="px-3 py-2 text-right font-medium">Gelesen</th>
            <th className="px-3 py-2 font-medium">Ergebnis</th>
          </tr>
        </thead>
        <tbody>
          {laeufe.map((l) => (
            <tr key={l.liste} className="border-b border-slate-100 last:border-0">
              <td className="px-3 py-2 align-top">{l.liste}</td>
              <td className="px-3 py-2 align-top whitespace-nowrap">
                {new Date(l.begonnenAm).toLocaleString('de-CH')}
              </td>
              <td className="px-3 py-2 text-right align-top">{l.gelesen ?? '–'}</td>
              <td className="px-3 py-2 align-top">
                {l.ergebnis === 'fehlgeschlagen' ? (
                  <span className="text-rose-800">{l.fehler || 'fehlgeschlagen'}</span>
                ) : l.ergebnis === 'erfolgreich' ? (
                  <span className="text-emerald-700">erfolgreich</span>
                ) : (
                  <span className="text-slate-500">läuft …</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Wer eine Rapport-Rolle hat, und womit seine Stunden vorbelegt sind.
 *
 * Die Liste entsteht aus den Anmeldungen und aus dem stündlichen
 * Gruppenabgleich – wer in einer Rapport-Gruppe steht, steht hier, auch
 * ohne je angemeldet gewesen zu sein.
 */
type Person = { id: string; name: string; email: string; standardTaetigkeitsartId: string | null };

function Mitarbeiter({
  leute,
  arten,
  fertig,
}: {
  leute: Person[];
  arten: TaetigkeitsartAnsicht[];
  fertig: () => void;
}) {
  const [fehler, setFehler] = useState<unknown>(null);
  const [suche, setSuche] = useState('');
  const waehlbar = arten.filter((t) => !t.deaktiviertAm && t.art === 'arbeit');
  /* Dieselbe Suche wie in jeder Mitarbeiterwahl: Name und E-Mail. */
  const treffer = leute.filter((m) => personPasst(m, suche));

  const setze = async (id: string, taetigkeitsartId: string | null) => {
    setFehler(null);

    try {
      await api.setzeStandardTaetigkeitsart(id, taetigkeitsartId);
      fertig();
    } catch (f) {
      setFehler(f);
    }
  };

  return (
    <>
      <Fehler fehler={fehler} />
      <input
        aria-label="Mitarbeiter suchen"
        className="feld mb-3"
        placeholder="Mitarbeiter suchen: Name oder E-Mail"
        autoComplete="off"
        value={suche}
        onChange={(e) => setSuche(e.target.value)}
      />
      {treffer.length === 0 && (
        <p className="text-slate-600">
          {leute.length === 0 ? 'Noch niemand hat eine Rolle.' : 'Niemand passt zur Suche.'}
        </p>
      )}
      <ul className="space-y-2">
        {treffer.map((m) => (
          <li key={m.id} className="karte">
            <div className="font-medium">
              <Personenzeile person={m} />
            </div>
            <label className="feldname mt-2" htmlFor={`vorbelegung-${m.id}`}>
              Vorbelegung
            </label>
            <select
              id={`vorbelegung-${m.id}`}
              className="feld sm:max-w-md"
              defaultValue={m.standardTaetigkeitsartId ?? ''}
              onChange={(e) => void setze(m.id, e.target.value || null)}
            >
              <option value="">– keine –</option>
              {waehlbar.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.gesellschaft} · {t.bezeichnung}
                </option>
              ))}
            </select>
          </li>
        ))}
      </ul>
    </>
  );
}

const BEREICHE = ['taetigkeitsarten', 'mitarbeiter', 'spiegel', 'einrichtung'] as const;
type Bereich = (typeof BEREICHE)[number];

const NAME: Record<Bereich, string> = {
  taetigkeitsarten: 'Tätigkeitsarten',
  mitarbeiter: 'Mitarbeiter',
  spiegel: 'Spiegel',
  einrichtung: 'Einrichtung',
};

export function Einstellungen() {
  const ich = useIch();
  const istAdmin = ich.rollen.includes('admin');

  const [bereich, setBereich] = useState<Bereich>('taetigkeitsarten');
  const [arten, setArten] = useState<TaetigkeitsartAnsicht[] | null>(null);
  const [leute, setLeute] = useState<Person[] | null>(null);
  const [laeufe, setLaeufe] = useState<Lauf[] | null>(null);
  const [einrichtung, setEinrichtung] = useState<Einrichtung | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);

  const laden = () => {
    api.taetigkeitsarten(undefined, true).then(setArten).catch(setFehler);
    api.mitarbeiter().then(setLeute).catch(setFehler);
    api.lauf().then(setLaeufe).catch(setFehler);
    api.einrichtung().then(setEinrichtung).catch(setFehler);
  };

  useEffect(laden, []);

  /*
   * Den Admin entscheidet die API bei jedem Aufruf – das hier ist nur
   * Bedienkomfort. Wer den Pfad von Hand eintippt, bekommt vom Server
   * ohnehin 403, und dann steht hier wenigstens ein Satz statt einer
   * leeren Seite.
   */
  if (!istAdmin) {
    return (
      <>
        <h1 className="mb-1 text-2xl font-semibold">Einstellungen</h1>
        <p className="text-slate-600">Diese Seite führt der Admin.</p>
      </>
    );
  }

  return (
    <>
      <h1 className="mb-1 text-2xl font-semibold">Einstellungen</h1>
      <p className="mb-4 text-slate-600">
        Was eingerichtet wird, und wie es um den Dienst steht. Nur für den Admin.
      </p>

      {/*
        Reiter und nicht vier Überschriften untereinander: Die Einrichtung
        sieht man einmal beim Aufsetzen, die Tätigkeitsarten ein paar Mal
        im Jahr – untereinander scrollte man an dem vorbei, weswegen man
        gekommen ist.
      */}
      <div className="mb-4 flex flex-wrap gap-2">
        {BEREICHE.map((b) => (
          <button
            key={b}
            type="button"
            aria-pressed={bereich === b}
            onClick={() => setBereich(b)}
            className={`min-h-0 rounded-full border px-3 py-1.5 text-sm font-medium ${
              bereich === b
                ? 'border-slate-900 bg-slate-900 text-white'
                : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-100'
            }`}
          >
            {NAME[b]}
          </button>
        ))}
      </div>

      <Fehler fehler={fehler} />

      {bereich === 'taetigkeitsarten' && <Taetigkeitsartenpflege arten={arten} fertig={laden} />}

      {bereich === 'mitarbeiter' &&
        (leute === null || arten === null ? (
          <p className="text-slate-600">Lädt …</p>
        ) : (
          <Mitarbeiter leute={leute} arten={arten} fertig={laden} />
        ))}

      {bereich === 'spiegel' && (
        <>
          <p className="mb-3 text-slate-600">
            Aufträge, Kunden, Artikel und Ansprechpartner kommen stündlich aus dem Hub. Steht der
            Lauf, sieht eine leere Auswahl genauso aus wie ein Kunde ohne Aufträge.
          </p>
          <SpiegelabgleichJetzt fertig={laden} />
          {laeufe === null ? <p className="text-slate-600">Lädt …</p> : <Spiegel laeufe={laeufe} />}
        </>
      )}

      {bereich === 'einrichtung' && (
        <>
          <p className="mb-3 text-slate-600">
            Die Werte stehen in <code>infra/.env</code> und gelten ab dem Neustart; kein Geheimnis
            steht hier – nur, ob eines gesetzt ist. Pflegen lässt sich hier nur, welche Gruppe
            welche Rolle bedeutet.
          </p>
          {einrichtung === null ? (
            <p className="text-slate-600">Lädt …</p>
          ) : (
            <Einrichtungsblock e={einrichtung} fertig={laden} />
          )}
        </>
      )}
    </>
  );
}
